// 📄 components/proyecto/ProjectStatsPanel.tsx
// Panel de estadísticas del proyecto con pestañas: "General" (config
// general + métricas globales) y una pestaña por cada solución activa del
// proyecto (Commerce, SLP, Sourcing, Contracts...). Vive arriba de las
// cards de soluciones en ProjectSolutionsHome.tsx.
//
// Puede comprimirse a una barra de una línea (CollapsedStatsBar) para no
// empujar el resto de la página — el estado se recuerda en localStorage.
import React, { useState } from 'react';
import { Settings, ChevronUp, FileDown } from 'lucide-react';
import { SOLUCIONES } from '../../pages/Proyectos/solucionesConfig.ts';
import { useProjectStats } from '../../hooks/useProjectStats.ts';
import { useProject } from '../../context/ProjectContext.tsx';
import { generarReporteProgresoPDF } from '../../services/reporteProyectoService.ts';
import GeneralStatsView from './GeneralStatsView.tsx';
import SolutionStatsView from './SolutionStatsView.tsx';
import CollapsedStatsBar from './CollapsedStatsBar.tsx';

const COLLAPSE_STORAGE_KEY = 'onehub_stats_panel_collapsed';

interface ProjectStatsPanelProps {
  projectId: string;
  erpTipo?: string;
  ambienteActivo?: string;
  solucionesActivas?: string[];
}

// Etiquetas cortas para que las pestañas no empujen el layout — la etiqueta
// larga (SOLUCIONES[slug].label) se sigue usando dentro de cada vista.
const TAB_SHORT_LABELS: Record<string, string> = {
  commerce: 'Commerce Autom.'
};

function readCollapsedPreference(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  } catch {
    // localStorage no disponible (modo privado, restricciones del entorno) — empieza expandido.
    return false;
  }
}

export default function ProjectStatsPanel({ projectId, erpTipo, ambienteActivo = 'DEV', solucionesActivas = [] }: ProjectStatsPanelProps) {
  const [activeTab, setActiveTab] = useState('general');
  const [isCollapsed, setIsCollapsed] = useState(readCollapsedPreference);
  const [exportando, setExportando] = useState(false);

  // Se ejecuta siempre, colapsado o no: la barra comprimida necesita los
  // mismos datos que la vista General, y así evitamos una segunda lectura
  // a Firestore cada vez que el usuario expande el panel de nuevo.
  const generalStats = useProjectStats(projectId, erpTipo);

  // "Reportes exportables" (2026-08-20, Fase 1) — usa el `project` del
  // contexto (ya montado, esta pieza vive bajo /proyecto/:projectId/*) en
  // vez de pedirlo por props, para no tener que tocar todos los callers
  // de ProjectStatsPanel solo para pasar nombre/cliente.
  const { project } = useProject();

  const handleExportar = async () => {
    if (!project || exportando) return;
    setExportando(true);
    try {
      await generarReporteProgresoPDF(project, generalStats, ambienteActivo);
    } catch (err) {
      console.error('Error generando el reporte PDF:', err);
    } finally {
      setExportando(false);
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      } catch {
        // Sin persistencia disponible — no bloquea el toggle.
      }
      return next;
    });
  };

  if (isCollapsed) {
    return <CollapsedStatsBar stats={generalStats} onExpand={toggleCollapse} />;
  }

  const solutionTabs = solucionesActivas
    .map((slug) => SOLUCIONES[slug])
    .filter(Boolean)
    .map((meta) => ({ id: meta.slug, icon: meta.icon, label: TAB_SHORT_LABELS[meta.slug] || meta.label, fullLabel: meta.label }));

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between gap-2 border-b border-white/10 mb-5">
        <div className="flex gap-1 overflow-x-auto">
          <TabButton active={activeTab === 'general'} onClick={() => setActiveTab('general')}>
            <Settings className="w-4 h-4" />
            General
          </TabButton>
          {solutionTabs.map((tab) => (
            <TabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
              <span aria-hidden="true">{tab.icon}</span>
              {tab.label}
            </TabButton>
          ))}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleExportar}
            disabled={!project || generalStats.loading || exportando}
            title="Exportar el progreso general del proyecto a PDF"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FileDown className="w-4 h-4" />
            {exportando ? 'Generando...' : 'Exportar PDF'}
          </button>
          <button
            onClick={toggleCollapse}
            aria-expanded={true}
            aria-label="Comprimir panel de estadísticas"
            title="Comprimir panel"
            className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-gray-300 transition-colors"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        </div>
      </div>

      {activeTab === 'general' ? (
        <GeneralStatsView stats={generalStats} ambienteActivo={ambienteActivo} />
      ) : (
        <SolutionStatsView
          key={activeTab}
          solutionId={activeTab}
          solutionLabel={solutionTabs.find((t) => t.id === activeTab)?.fullLabel || activeTab}
          projectId={projectId}
          onBack={() => setActiveTab('general')}
        />
      )}
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
        active
          ? 'border-blue-500 text-blue-400 font-medium'
          : 'border-transparent text-gray-500 hover:text-gray-300'
      }`}
    >
      {children}
    </button>
  );
}
